import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core"
import {
  Stack,
  Text,
  Divider,
  Box,
  Group,
  useMantineTheme,
} from "@mantine/core"
import { IconColumns2, IconGripVertical } from "@tabler/icons-react"
import { Table } from "@tanstack/react-table"
import { useState } from "react"

import { Button, ButtonProps } from "../../atoms/Button/Button"
import { SearchInput } from "../../molecules/SearchInput/SearchInput"
import { Menu } from "../../organisms/Menu/Menu"

import { DroppableArea } from "./components/DroppableArea"
import { ColumnManagerTranslationFallbackKeys } from "./config"
import {
  useGetActiveColumn,
  useInitDndContext,
  useGetFilteredColumns,
  useGetColumnMap,
} from "./hooks"
import { getColumnLabel, useControlledState } from "./utils"

export type ColumnManagerProps<T> = {
  table: Table<T>
  isOpen?: boolean
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>
  translations: Record<
    keyof typeof ColumnManagerTranslationFallbackKeys,
    string
  >
} & ButtonProps

export const ColumnManager = <T,>({
  table,
  isOpen: controlledIsOpen,
  setIsOpen: controlledSetIsOpen,
  variant = "ghost",
  translations,
  ...props
}: ColumnManagerProps<T>) => {
  const [isOpen, setIsOpen] = useControlledState(
    controlledIsOpen,
    controlledSetIsOpen
  )
  const [searchQuery, setSearchQuery] = useState("")
  const theme = useMantineTheme()
  const columnMap = useGetColumnMap(table)

  const {
    filteredVisibleColumns,
    filteredHiddenColumns,
    filteredPinnedColumns,
  } = useGetFilteredColumns(table, searchQuery)

  const { sensors, activeId, handleDragStart, handleDragEnd } =
    useInitDndContext(table)
  const activeColumn = useGetActiveColumn(columnMap, activeId)

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[]}
    >
      <Menu
        closeOnItemClick={false}
        position="bottom-start"
        opened={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
      >
        <Menu.Target>
          <Button
            variant={variant}
            px="xs"
            rightSection={
              <IconColumns2
                stroke={theme.other.icon.stroke}
                size={theme.other.icon.size}
              />
            }
            {...props}
          >
            {translations.columns ??
              ColumnManagerTranslationFallbackKeys.columns}
          </Button>
        </Menu.Target>
        <Menu.Dropdown
          p="xs"
          w="fit-content"
          miw="100px"
          maw={300}
          mah="70vh"
          style={{
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Stack gap="xs">
            <SearchInput
              placeholder={
                translations.searchPlaceholder ??
                ColumnManagerTranslationFallbackKeys.searchPlaceholder
              }
              inputValue={searchQuery}
              onChange={(value) => {
                setSearchQuery(value)
              }}
            />

            <DroppableArea
              id="visible"
              columns={filteredVisibleColumns}
              pinnedColumns={filteredPinnedColumns}
              title={
                translations.visibleColumns ??
                ColumnManagerTranslationFallbackKeys.visibleColumns
              }
              count={filteredVisibleColumns.length}
              translations={translations}
            />

            <Divider />

            <DroppableArea
              id="hidden"
              columns={filteredHiddenColumns}
              title={
                translations.hiddenColumns ??
                ColumnManagerTranslationFallbackKeys.hiddenColumns
              }
              count={filteredHiddenColumns.length}
              translations={translations}
            />
          </Stack>
        </Menu.Dropdown>
      </Menu>

      {/* DragOverlay OUTSIDE the Menu - this is critical! */}
      <DragOverlay dropAnimation={null}>
        {activeColumn ? (
          <Box
            p="xs"
            bg="white"
            style={{
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              borderRadius: "4px",
              cursor: "grabbing",
            }}
          >
            <Group gap="xs">
              <IconGripVertical size={14} stroke={1.5} />
              <Text>{getColumnLabel(activeColumn)}</Text>
            </Group>
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
